import { useEffect, useState } from 'react'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import {
  Busy,
  ErrorBanner,
  Field,
  MultiReferenceSelect,
  Notice,
  NumberInput,
  ReferenceSelect,
  TextArea,
  TextInput,
  YesNo,
} from '../ui/controls'
import type { Gender, LocationItem, Profile, ProfileReferenceCatalogue, ProfileUpdate } from '../api/types'

/**
 * The profile form.
 *
 * Every dropdown is driven by `/api/reference/profile`, which sends `nameEn` and `nameZh` for
 * every row — so switching language re-renders and never refetches, and this file has no list of
 * options in it to fall out of step with the database.
 *
 * Family name and given name are written here and never read back: `GET /api/profile/me` does
 * not return them, because 决议 §2.1 releases a name only when a connection forms. The form
 * shows them empty on load for that reason, and only sends them when they are filled in.
 */
export function ProfilePage() {
  const { t } = useI18n()

  const [catalogue, setCatalogue] = useState<ProfileReferenceCatalogue | null>(null)
  const [locations, setLocations] = useState<LocationItem[]>([])
  const [profile, setProfile] = useState<Profile | null>(null)
  const [draft, setDraft] = useState<ProfileUpdate | null>(null)
  const [names, setNames] = useState({ familyName: '', givenName: '' })
  const [citizenships, setCitizenships] = useState<string[]>([])
  const [languages, setLanguages] = useState<string[]>([])
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    Promise.all([api.reference.profile(), api.reference.locations()])
      .then(([cat, locs]) => {
        setCatalogue(cat)
        setLocations(locs)
      })
      .catch(setError)

    api.profile
      .get()
      .then((loaded) => {
        setProfile(loaded)
        setDraft({ version: loaded.version, ...toDraft(loaded) })
        setCitizenships(loaded.citizenshipCountryIds)
        setLanguages(loaded.languages.map((l) => l.languageId))
      })
      .catch((cause) => {
        // 404 is the onboarding case: an account exists and a profile does not yet. Group 17B
        // sealed that behaviour on purpose, so a blank form is the right answer, not an error.
        if (isNotFound(cause)) {
          setProfile(null)
          setDraft({ version: 0 })
          return
        }

        setError(cause)
      })
  }, [])

  if (!catalogue || !draft) {
    return <Busy />
  }

  const set = <K extends keyof ProfileUpdate>(key: K, value: ProfileUpdate[K]) => {
    setDraft({ ...draft, [key]: value })
    setSaved(false)
  }

  const save = (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setBusy(true)

    const body: ProfileUpdate = {
      ...draft,
      familyName: names.familyName || undefined,
      givenName: names.givenName || undefined,
    }

    api.profile
      .update(body)
      .then((outcome) =>
        // The lists are separate endpoints (17B) and each carries the version forward.
        api.profile
          .citizenships(citizenships)
          .then(() => api.profile.languages(languages.map((id) => ({ languageId: id }))))
          .then(() => outcome),
      )
      .then(() => api.profile.get())
      .then((reloaded) => {
        setProfile(reloaded)
        setDraft({ version: reloaded.version, ...toDraft(reloaded) })
        setSaved(true)
      })
      .catch(setError)
      .finally(() => setBusy(false))
  }

  return (
    <form className="page stack" onSubmit={save}>
      <h1>{t('profile.title')}</h1>

      <ErrorBanner error={error} />
      {saved ? <Notice>{t('common.saved')}</Notice> : null}

      <section className="card">
        <h2>{t('profile.section.basics')}</h2>

        <Field label={t('profile.displayName')} required>
          <TextInput value={draft.displayName ?? ''} onChange={(v) => set('displayName', v)} maxLength={50} />
        </Field>

        <Field label={t('profile.familyName')} hint={t('profile.privateNote')} required>
          <TextInput
            value={names.familyName}
            onChange={(v) => setNames({ ...names, familyName: v })}
            maxLength={100}
          />
        </Field>

        <Field label={t('profile.givenName')} required>
          <TextInput
            value={names.givenName}
            onChange={(v) => setNames({ ...names, givenName: v })}
            maxLength={100}
          />
        </Field>

        <Field label={t('profile.gender')} required>
          <select
            className="control"
            value={draft.gender ?? ''}
            onChange={(event) =>
              set('gender', event.target.value === '' ? null : (event.target.value as Gender))
            }
          >
            <option value="">{t('common.choose')}</option>
            <option value="MALE">{t('profile.gender.Male')}</option>
            <option value="FEMALE">{t('profile.gender.Female')}</option>
          </select>
        </Field>

        <Field label={t('profile.dateOfBirth')} required>
          <TextInput
            value={draft.dateOfBirth ?? ''}
            onChange={(v) => set('dateOfBirth', v || null)}
            type="date"
          />
        </Field>

        <Field label={t('profile.heightCm')} required>
          <NumberInput value={draft.heightCm ?? null} onChange={(v) => set('heightCm', v)} min={100} max={250} />
        </Field>

        <Field label={t('profile.location')} required>
          <select
            className="control"
            value={draft.locationId ?? ''}
            onChange={(event) => set('locationId', event.target.value || null)}
          >
            <option value="">{t('common.choose')}</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.nameZh ? `${l.nameZh} / ${l.nameEn}` : l.nameEn}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('profile.relocation')} required>
          <YesNo value={draft.relocationWithinNz ?? null} onChange={(v) => set('relocationWithinNz', v)} />
        </Field>
      </section>

      <section className="card">
        <h2>{t('profile.section.life')}</h2>

        <Field label={t('profile.nzStatus')} required>
          <ReferenceSelect
            value={draft.nzStatusId ?? null}
            onChange={(v) => set('nzStatusId', v)}
            items={catalogue.nzStatuses}
          />
        </Field>

        <Field label={t('profile.maritalStatus')} required>
          <ReferenceSelect
            value={draft.maritalStatusId ?? null}
            onChange={(v) => set('maritalStatusId', v)}
            items={catalogue.maritalStatuses}
          />
        </Field>

        <Field label={t('profile.smoking')} required>
          <ReferenceSelect
            value={draft.smokingStatusId ?? null}
            onChange={(v) => set('smokingStatusId', v)}
            items={catalogue.smokingStatuses}
          />
        </Field>

        <Field label={t('profile.alcohol')} required>
          <ReferenceSelect
            value={draft.alcoholStatusId ?? null}
            onChange={(v) => set('alcoholStatusId', v)}
            items={catalogue.alcoholStatuses}
          />
        </Field>

        <Field label={t('profile.religion')} required>
          <ReferenceSelect
            value={draft.religionId ?? null}
            onChange={(v) => set('religionId', v)}
            items={catalogue.religions}
          />
        </Field>
      </section>

      <section className="card">
        <h2>{t('profile.section.work')}</h2>

        <Field label={t('profile.education')} required>
          <ReferenceSelect
            value={draft.educationLevelId ?? null}
            onChange={(v) => set('educationLevelId', v)}
            items={catalogue.educationLevels}
          />
        </Field>

        <Field label={t('profile.employment')} required>
          <ReferenceSelect
            value={draft.employmentStatusId ?? null}
            onChange={(v) => set('employmentStatusId', v)}
            items={catalogue.employmentStatuses}
          />
        </Field>

        <Field label={t('profile.occupation')}>
          <ReferenceSelect
            value={draft.occupationCategoryId ?? null}
            onChange={(v) => set('occupationCategoryId', v)}
            items={catalogue.occupationCategories}
          />
        </Field>
      </section>

      <section className="card">
        <h2>{t('profile.section.about')}</h2>

        <Field label={t('profile.selfIntroduction')}>
          <TextArea
            value={draft.selfIntroduction ?? ''}
            onChange={(v) => set('selfIntroduction', v)}
            maxLength={2000}
          />
        </Field>

        <Field label={t('profile.hobbies')}>
          <TextArea value={draft.hobbiesText ?? ''} onChange={(v) => set('hobbiesText', v)} maxLength={1000} />
        </Field>
      </section>

      <section className="card">
        <h2>{t('profile.section.basics')}</h2>

        <Field label="Citizenship" required>
          <MultiReferenceSelect values={citizenships} onChange={setCitizenships} items={catalogue.countries} />
        </Field>

        <Field label="Languages" required>
          <MultiReferenceSelect values={languages} onChange={setLanguages} items={catalogue.languages} />
        </Field>
      </section>

      <button type="submit" className="primary" disabled={busy}>
        {busy ? t('common.saving') : t('common.save')}
      </button>

      {profile?.completedAt ? <Notice>{t('completion.ready')}</Notice> : null}
    </form>
  )
}

function toDraft(profile: Profile): Omit<ProfileUpdate, 'version'> {
  const { version: _version, citizenshipCountryIds: _c, languages: _l, completedAt: _d, ...rest } = profile

  return rest
}

function isNotFound(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: number }).status === 404
  )
}
